import { useState } from 'react';
import { Tooltip, IconButton, Menu, TextField, Button, Typography } from '@mui/material';
import { useSlate } from 'slate-react';
import { Transforms, Editor, Element as SlateElement } from 'slate';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

const NumberedListButton = () => {
    const editor = useSlate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [startValue, setStartValue] = useState(1);
    const open = Boolean(anchorEl);

    // Add these helper functions that are used in the component
    const isBlockActive = (editor, format, blockType = 'type') => {
        const { selection } = editor;
        if (!selection) return false;

        const [match] = Array.from(
            Editor.nodes(editor, {
                at: Editor.unhangRange(editor, selection),
                match: n =>
                    !Editor.isEditor(n) &&
                    SlateElement.isElement(n) &&
                    n[blockType] === format,
            })
        );
        return !!match;
    };

    const toggleBlock = (editor, format, options = {}) => {
        const isActive = isBlockActive(editor, format, 'type');
        const isList = format === 'numbered-list' || format === 'bulleted-list';

        // Unwrap existing lists if toggling off
        Transforms.unwrapNodes(editor, {
            match: (n) =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                (n.type === 'numbered-list' || n.type === 'bulleted-list'),
            split: true,
        });

        let newProperties;
        newProperties = {
            type: isActive ? 'paragraph' : isList ? 'list-item' : format,
        };

        // If we're creating a numbered list and a start value was provided
        if (format === 'numbered-list' && options.start) {
            newProperties.start = options.start;
        }

        // Apply changes to nodes
        Transforms.setNodes(editor, newProperties);

        // Wrap selection inside list if turning it on
        if (!isActive && isList) {
            const block = {
                type: format,
                children: [],
                ...(format === 'numbered-list' && options.start ? { start: options.start } : {})
            };
            Transforms.wrapNodes(editor, block);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    const handleStartValueChange = (e) => {
        const value = e.target.value;

        // Allow empty string temporarily while typing
        if (value === "") {
            setStartValue("");
            return;
        }

        // Parse and fallback to 1 if invalid
        const parsed = parseInt(value, 10);
        if (isNaN(parsed) || parsed < 1) {
            setStartValue(1);
        } else {
            setStartValue(parsed);
        }
    };

    // Ensure default reset when user leaves the field
    const handleBlur = () => {
        if (startValue === "" || startValue < 1) {
            setStartValue(1);
        }
    };

    const handleApply = () => {
        handleClose();

        // First activate numbered list if not active
        if (!isBlockActive(editor, 'numbered-list')) {
            toggleBlock(editor, 'numbered-list', { start: startValue });
        } else {
            // Update existing numbered list with new start value
            Transforms.setNodes(
                editor,
                { start: startValue },
                {
                    match: n => SlateElement.isElement(n) && n.type === 'numbered-list'
                }
            );
        }
    };

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title="Numbered List" arrow>
                <button
                    type="button"
                    active={isBlockActive(editor, 'numbered-list')}
                    onMouseDown={(event) => {
                        event.preventDefault();
                        toggleBlock(editor, 'numbered-list');
                    }}
                    className={`text-center justify-center flex ${isBlockActive(editor, 'numbered-list')
                        ? 'text-black font-bold'  // active state
                        : 'text-[#cccccc]'           // inactive state
                        }`}
                >
                    <FormatListNumberedIcon sx={{ fontSize: 18 }} />
                </button>
            </Tooltip>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{
                    p: 0,
                    height: '14px',
                    width: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '2px',
                    ml: '-1px',
                    '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#bdbdbd'
                    }
                }}
            >
                {open ? (
                    <ArrowDropUpIcon fontSize="small" sx={{ fontSize: '16px' }} />
                ) : (
                    <ArrowDropDownIcon fontSize="small" sx={{ fontSize: '16px' }} />
                )}
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: { p: 2, width: 200 }
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    List Options
                </Typography>
                <TextField
                    label="Start from"
                    type="number"
                    value={startValue}
                    onChange={handleStartValueChange}
                    onBlur={handleBlur}
                    inputProps={{ min: 1 }}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleApply}
                    fullWidth
                >
                    Apply
                </Button>
            </Menu>
        </div>
    );
};

export default NumberedListButton;